import { css } from '@emotion/react';
import { ChallengeCourseCard, CourseCard } from '@inflearn/course-card';
import type { Language } from '@inflearn/i18n-tools';
import type { MouseEvent } from 'react';

import { type CourseCardItem, CourseType } from '../../../resources/@types/courseSchema';
import { getCourseDetailUrl } from '../../utils/course/getCourseDetailUrl';

interface CourseCardLinkProps {
  course: CourseCardItem;
  lang: Language;
  isAdmin?: boolean;
  isInstructor?: boolean;
  isHidePrice?: boolean;
  thumbLoading?: 'eager' | 'lazy';
  onClickLikeButton: (event: MouseEvent<HTMLButtonElement>) => void;
  onClickCartButton?: (event: MouseEvent<HTMLButtonElement>) => void;
  onClickMoveToCartButton?: (event: MouseEvent<HTMLButtonElement>) => void;
}

export const CourseCardLink = ({
  course,
  lang,
  isAdmin = false,
  isInstructor = false,
  isHidePrice,
  thumbLoading,
  onClickLikeButton,
  onClickCartButton,
  onClickMoveToCartButton,
}: CourseCardLinkProps) => {
  const courseDetailUrl = getCourseDetailUrl({
    type: course.type,
    courseSlug: course.course.slug,
  });

  // 카드 타입은 게시일을 문자열로 요구하는데 계약은 null 을 허용한다(미게시 강의)
  const cardCourse = { ...course.course, publishedAt: course.course.publishedAt ?? '' };

  /*
   * 호버 카드는 floating-ui 포털로 body 에 붙어 이 <a> 바깥에 있다. 그래서 호버 상태에서의 클릭은
   * 링크로 이어지지 않고, 카드가 preventDefault 한 클릭도 여기서 직접 열어 줘야 한다.
   */
  const moveToCourseDetail = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    window.open(courseDetailUrl, '_blank');
  };

  return (
    <a href={courseDetailUrl} css={styleCardLink} onClick={moveToCourseDetail}>
      {course.type === CourseType.CHALLENGE && course.challengeCourse ? (
        <ChallengeCourseCard
          {...cardCourse}
          {...course.challengeCourse}
          id={course.id}
          listPrice={course.listPrice}
          instructor={course.instructor}
          onClick={moveToCourseDetail}
        />
      ) : (
        <CourseCard
          {...course}
          course={cardCourse}
          lang={lang}
          type={course.type === CourseType.OFF ? 'OFF' : 'ON'}
          // 가격을 감추면 정가·할인율·할인 기간 표기도 함께 사라진다
          isHidePrice={isHidePrice ?? course.isHidePrice}
          isHideInstructorName={false}
          isAdmin={isAdmin}
          isInstructor={isInstructor}
          thumbLoading={thumbLoading}
          onClick={moveToCourseDetail}
          onClickMoveToCourseDetailButton={moveToCourseDetail}
          onClickLikeButton={onClickLikeButton}
          onClickCartButton={onClickCartButton}
          onClickMoveToCartButton={onClickMoveToCartButton}
        />
      )}
    </a>
  );
};

const styleCardLink = css({
  display: 'block',
  color: 'inherit',
  textDecoration: 'none',
});
